/**
 * Phase 2a · V2 Preview / Admin Review Screen — READ-ONLY.
 *
 * Every value is displayed inside a bucket chip. PAN / mobile / email /
 * aadhaar are masked server-side. This page NEVER creates an assessment,
 * NEVER calls the LLM, NEVER sends a letter. It exists so a reviewer can
 * see exactly what the deterministic V2 engine would produce for a given
 * V1 check, without touching V1 in any way.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminEligibilityV2 } from "../lib/api";

const BUCKET_STYLES = {
  verified: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  reported: "bg-sky-500/15 text-sky-200 border-sky-500/40",
  calculated: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  missing: "bg-neutral-500/15 text-neutral-300 border-neutral-500/40",
  inconsistent: "bg-rose-500/15 text-rose-200 border-rose-500/40",
};

const BucketChip = ({ bucket }) => (
  <span
    data-testid={`v2-bucket-${bucket}`}
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
      BUCKET_STYLES[bucket] || BUCKET_STYLES.missing
    }`}
  >
    {bucket}
  </span>
);

const FieldRow = ({ label, field, testid }) => (
  <div
    data-testid={testid}
    className="grid grid-cols-1 gap-2 border-b border-white/5 py-3 sm:grid-cols-[220px_1fr_auto] sm:items-start"
  >
    <div className="text-sm font-medium text-neutral-400">{label}</div>
    <div className="min-w-0">
      <div className="break-words text-sm text-neutral-100">
        {field?.raw_present ? String(field.value ?? "—") : (
          <span className="italic text-neutral-500">not on file</span>
        )}
      </div>
      {field?.notes ? (
        <div className="mt-1 text-xs text-neutral-500">{field.notes}</div>
      ) : null}
      <div className="mt-1 text-[11px] text-neutral-500">source: {field?.source}</div>
    </div>
    <div className="flex justify-start sm:justify-end">
      <BucketChip bucket={field?.bucket} />
    </div>
  </div>
);

const Section = ({ title, children, testid }) => (
  <section
    data-testid={testid}
    className="rounded-xl border border-white/10 bg-neutral-950/60 p-5 shadow-lg backdrop-blur"
  >
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.15em] text-amber-300">
      {title}
    </h2>
    {children}
  </section>
);

export default function AdminEligibilityV2Preview() {
  const { checkId } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await AdminEligibilityV2.preview(checkId);
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === "object" ? detail.message : detail || e.message;
        if (!cancelled) setState({ loading: false, error: msg, data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [checkId]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#0b0f1a] p-6 text-neutral-200" data-testid="v2-preview-loading">
        Loading V2 preview…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[#0b0f1a] p-6 text-neutral-200" data-testid="v2-preview-error">
        <div className="mx-auto max-w-2xl rounded-xl border border-rose-500/40 bg-rose-950/40 p-6">
          <h1 className="text-lg font-semibold text-rose-200">V2 Preview unavailable</h1>
          <p className="mt-2 text-sm text-rose-100/80">{state.error}</p>
          <Link
            to="/admin/eligibility-checks"
            className="mt-4 inline-block rounded border border-amber-400/40 px-3 py-1 text-xs text-amber-200 hover:bg-amber-400/10"
          >
            ← Back to Eligibility Checks
          </Link>
        </div>
      </div>
    );
  }

  const { data } = state;
  const kyc = data.kyc || {};
  const cash = data.cash_flow || {};
  const engine = data.engine || {};
  const pay = data.payment_integrity || {};

  return (
    <div className="min-h-screen bg-[#0b0f1a] p-4 text-neutral-200 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-5">
        {/* Banner */}
        <div
          data-testid="v2-preview-banner"
          className="rounded-lg border border-amber-400/60 bg-amber-500/10 px-4 py-3 text-center text-sm font-bold uppercase tracking-widest text-amber-200"
        >
          {data.banner}
        </div>

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white">
              V2 Preview · <span className="text-amber-300">{data.check?.loan_type_label || data.check?.loan_type}</span>
            </h1>
            <p className="text-xs text-neutral-500">
              Check ID: <span className="font-mono">{data.check?.id}</span>
              {" · "}Created {data.check?.created_at?.slice(0, 19)?.replace("T", " ")}
              {" · "}Payment: {data.check?.payment_status || "—"}
            </p>
          </div>
          <Link
            to="/admin/eligibility-checks"
            data-testid="v2-preview-back-link"
            className="rounded border border-white/10 px-3 py-1 text-xs text-neutral-300 hover:bg-white/5"
          >
            ← Back
          </Link>
        </div>

        {/* Feature-flag chips */}
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="rounded-full border border-white/10 bg-neutral-900 px-2 py-0.5 text-neutral-400">
            ELIGIBILITY_V2_ENABLED = {String(data.meta?.flags?.ELIGIBILITY_V2_ENABLED)}
          </span>
          <span className="rounded-full border border-white/10 bg-neutral-900 px-2 py-0.5 text-neutral-400">
            SECURITY_BACKED_ROUTE_ENABLED = {String(data.meta?.flags?.SECURITY_BACKED_ROUTE_ENABLED)}
          </span>
          <span className="rounded-full border border-white/10 bg-neutral-900 px-2 py-0.5 text-neutral-400">
            Adapter {data.meta?.adapter_version}
          </span>
        </div>

        {/* KYC Section */}
        <Section title="Identity & KYC (masked)" testid="v2-section-kyc">
          <FieldRow label="Full Name" field={kyc.full_name} testid="v2-field-name" />
          <FieldRow label="Date of Birth" field={kyc.date_of_birth} testid="v2-field-dob" />
          <FieldRow label="PAN" field={kyc.pan} testid="v2-field-pan" />
          <FieldRow label="Aadhaar (last 4)" field={kyc.aadhaar_last4} testid="v2-field-aadhaar" />
          <FieldRow label="Mobile" field={kyc.mobile} testid="v2-field-mobile" />
          <FieldRow label="Email" field={kyc.email} testid="v2-field-email" />
          <FieldRow label="Residence Address" field={kyc.residence_address} testid="v2-field-address" />
          <FieldRow label="Business Name" field={kyc.business_name} testid="v2-field-bizname" />
          <FieldRow label="Business Type" field={kyc.business_type} testid="v2-field-biztype" />
          <FieldRow label="GSTIN" field={kyc.gstin} testid="v2-field-gstin" />
          <FieldRow label="Udyam Number" field={kyc.udyam_number} testid="v2-field-udyam" />
          <FieldRow label="Vintage (years)" field={kyc.vintage_years} testid="v2-field-vintage" />
        </Section>

        {/* Cash Flow Section */}
        <Section title="Income & Cash Flow" testid="v2-section-cash">
          <FieldRow label="Verified Monthly Income" field={cash.verified_monthly_income}
                    testid="v2-field-verified-income" />
          <FieldRow label="Reported Monthly Income" field={cash.reported_monthly_income}
                    testid="v2-field-reported-income" />
          <FieldRow label="Existing Monthly EMI" field={cash.existing_monthly_emi}
                    testid="v2-field-existing-emi" />
        </Section>

        {/* Documents */}
        <Section title={`Documents on File (${(data.documents_summary || []).length})`}
                 testid="v2-section-documents">
          {(data.documents_summary || []).length === 0 ? (
            <p className="text-sm italic text-neutral-500">No documents uploaded yet.</p>
          ) : (
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {data.documents_summary.map((d) => (
                <li key={d.sno} data-testid={`v2-doc-${d.sno}`}
                    className="rounded border border-white/10 bg-neutral-900 px-3 py-2 text-sm">
                  <div className="font-medium text-neutral-100">{d.label}</div>
                  <div className="text-[11px] text-neutral-500">
                    kind: {d.kind || "—"} · {d.has_file ? "stored" : "no file"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Payment Integrity */}
        <Section title="Payment Integrity (server-side re-check)" testid="v2-section-payment">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span data-testid="v2-payment-status"
                  className={`rounded-full border px-2 py-0.5 text-xs font-semibold uppercase ${
                    pay.verified
                      ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200"
                      : "border-rose-500/40 bg-rose-500/15 text-rose-200"
                  }`}>
              {pay.verified ? "verified" : "not verified"}
            </span>
            <span className="text-neutral-400">status: {pay.status || "—"}</span>
            {pay.reason ? <span className="text-neutral-500">· reason: {pay.reason}</span> : null}
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Payment status is informational for the reviewer. It does not influence
            FOIR, eligibility amount or the risk tier.
          </p>
        </Section>

        {/* Deterministic engine block */}
        <Section title="Deterministic Engine Output" testid="v2-section-engine">
          {engine.no_active_policy ? (
            <div data-testid="v2-no-active-policy"
                 className="rounded border border-amber-500/40 bg-amber-500/10 p-4 text-center text-sm font-semibold uppercase tracking-wider text-amber-200">
              {engine.note}
            </div>
          ) : (
            <>
              <div className="mb-3 rounded border border-white/10 bg-neutral-900 p-3 text-xs text-neutral-400">
                Policy: <span className="text-neutral-200">{engine.policy?.id}</span>
                {" · v"}{engine.policy?.version}
                {" · product="}{engine.policy?.product}
                {" · max_foir="}{engine.policy?.max_foir}
                {" · multiplier="}{engine.policy?.eligibility_multiplier}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded border border-white/10 bg-neutral-900 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500">Reason Code</div>
                  <div data-testid="v2-engine-reason"
                       className={`mt-1 text-sm font-semibold ${
                         engine.outputs?.reason_code === "OK"
                           ? "text-emerald-300" : "text-amber-300"
                       }`}>
                    {engine.outputs?.reason_code}
                  </div>
                </div>
                <div className="rounded border border-white/10 bg-neutral-900 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500">Quantified?</div>
                  <div data-testid="v2-engine-quantified"
                       className="mt-1 text-sm font-semibold text-neutral-100">
                    {engine.outputs?.quantified ? "yes" : "no"}
                  </div>
                </div>
                <div className="rounded border border-white/10 bg-neutral-900 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                    Eligible amount (indicative only)
                  </div>
                  <div data-testid="v2-engine-eligible-amount"
                       className="mt-1 text-lg font-semibold text-amber-300">
                    {engine.outputs?.eligible_amount
                      ? `₹ ${engine.outputs.eligible_amount}`
                      : <span className="italic text-neutral-500">not quantified</span>}
                  </div>
                </div>
                <div className="rounded border border-white/10 bg-neutral-900 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500">FOIR</div>
                  <div data-testid="v2-engine-foir"
                       className="mt-1 text-sm font-semibold text-neutral-100">
                    {engine.outputs?.foir ?? "—"}
                  </div>
                </div>
                <div className="rounded border border-white/10 bg-neutral-900 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500">Monthly Surplus</div>
                  <div data-testid="v2-engine-surplus"
                       className="mt-1 text-sm font-semibold text-neutral-100">
                    {engine.outputs?.monthly_surplus ?? "—"}
                  </div>
                </div>
                <div className="rounded border border-white/10 bg-neutral-900 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500">Proposed EMI Max</div>
                  <div data-testid="v2-engine-proposed-emi"
                       className="mt-1 text-sm font-semibold text-neutral-100">
                    {engine.outputs?.proposed_emi_max ?? "—"}
                  </div>
                </div>
              </div>
              <details className="mt-3 text-xs text-neutral-400">
                <summary className="cursor-pointer text-neutral-300">
                  Calculation trace (audit)
                </summary>
                <pre data-testid="v2-engine-trace"
                     className="mt-2 overflow-x-auto rounded bg-black/50 p-3 text-[11px] leading-relaxed">
                  {JSON.stringify(engine.outputs?.trace || {}, null, 2)}
                </pre>
              </details>
            </>
          )}
        </Section>

        {/* Footer disclaimer */}
        <p data-testid="v2-preview-footer" className="text-center text-[11px] italic text-neutral-500">
          Phase 2a preview · No PDF is generated · No email is sent · V1 record is unchanged.
        </p>
      </div>
    </div>
  );
}
